import React from "react";

const ZONES = [
  { lo: -100, hi: -75, color: "#8B2E20" },
  { lo: -75, hi: -45, color: "#C66B59" },
  { lo: -45, hi: -15, color: "#E89B8B" },
  { lo: -15, hi: 15, color: "#9C9F92" },
  { lo: 15, hi: 45, color: "#B5D4B5" },
  { lo: 45, hi: 75, color: "#7BC79E" },
  { lo: 75, hi: 100, color: "#3F7A56" },
];

/**
 * Semicircular gauge meter. Score range -100..+100 mapped to 180° arc.
 * Width auto-sizes to the parent container.
 */
export default function SentimentMeter({ score = 0, label = "Neutral", size = "md" }) {
  const safe = Math.max(-100, Math.min(100, Number(score) || 0));
  // Map score -100..+100 → angle -90..+90 (degrees from 12 o'clock)
  const angle = (safe / 100) * 90;
  const dims = size === "sm" ? { w: 160, h: 96, r: 70, cx: 80, cy: 80, stroke: 14 } : { w: 220, h: 130, r: 96, cx: 110, cy: 110, stroke: 18 };
  const { w, h, r, cx, cy, stroke } = dims;

  // Build arc paths for each zone
  const arc = (startScore, endScore, color) => {
    const startA = (startScore / 100) * 90 - 90; // -180..0 mapped to scores -100..+100
    const endA = (endScore / 100) * 90 - 90;
    const sx = cx + r * Math.cos((startA * Math.PI) / 180);
    const sy = cy + r * Math.sin((startA * Math.PI) / 180);
    const ex = cx + r * Math.cos((endA * Math.PI) / 180);
    const ey = cy + r * Math.sin((endA * Math.PI) / 180);
    return (
      <path
        key={`${startScore}-${endScore}`}
        d={`M ${sx} ${sy} A ${r} ${r} 0 0 1 ${ex} ${ey}`}
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="butt"
        fill="none"
      />
    );
  };

  // Needle endpoint: from center to r*0.92, rotated by angle (relative to 12 o'clock)
  const needleA = (angle - 90) * (Math.PI / 180);
  const needleX = cx + r * 0.92 * Math.cos(needleA);
  const needleY = cy + r * 0.92 * Math.sin(needleA);

  return (
    <div className="flex flex-col items-center" data-testid="sentiment-meter">
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h * 1.05}`} className="overflow-visible">
        {ZONES.map((z) => arc(z.lo, z.hi, z.color))}
        {/* Tick marks at zone boundaries */}
        {[-75, -45, -15, 15, 45, 75].map((s) => {
          const a = ((s / 100) * 90 - 90) * (Math.PI / 180);
          const x1 = cx + (r - stroke / 2 - 2) * Math.cos(a);
          const y1 = cy + (r - stroke / 2 - 2) * Math.sin(a);
          const x2 = cx + (r + stroke / 2 + 2) * Math.cos(a);
          const y2 = cy + (r + stroke / 2 + 2) * Math.sin(a);
          return <line key={s} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#FFFFFF" strokeWidth="1.5" />;
        })}
        {/* Needle */}
        <line
          x1={cx}
          y1={cy}
          x2={needleX}
          y2={needleY}
          stroke="#2A2F2B"
          strokeWidth={size === "sm" ? 2.5 : 3.5}
          strokeLinecap="round"
        />
        <circle cx={cx} cy={cy} r={size === "sm" ? 5 : 7} fill="#2A2F2B" />
        <circle cx={cx} cy={cy} r={size === "sm" ? 2 : 3} fill="#FFFFFF" />
      </svg>
      <div className="text-center -mt-2">
        <div
          className="font-mono-ticker text-3xl font-bold tabular-nums"
          style={{ color: ZONES.find((z) => z.lo <= safe && safe < z.hi)?.color || "#9C9F92" }}
          data-testid="sentiment-meter-score"
        >
          {safe >= 0 ? "+" : ""}
          {safe.toFixed(0)}
        </div>
        <div
          className="text-xs uppercase tracking-[0.2em] font-semibold mt-0.5 text-[#2A2F2B]"
          data-testid="sentiment-meter-label"
        >
          {label}
        </div>
      </div>
    </div>
  );
}
